import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import './Check.css';

// Firebase imports
import { db, auth, collection, addDoc, getDocs, query, where } from './firebase';

// Lucide Icons
import { Angry, Frown, Meh, Smile, Laugh, Mic, MicOff } from 'lucide-react';

const MOOD_LABELS = {
  1: "Very Poor",
  2: "Poor",
  3: "Neutral",
  4: "Good",
  5: "Very Good"
};

const MOOD_ICONS = {
  1: <Angry color="#ff4d4f" size={24} title="Very Poor" />,
  2: <Frown color="#ff7a45" size={24} title="Poor" />,
  3: <Meh color="#faad14" size={24} title="Neutral" />,
  4: <Smile color="#52c41a" size={24} title="Good" />,
  5: <Laugh color="#1890ff" size={24} title="Very Good" />
};

const CheckInPage = () => {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [moodRating, setMoodRating] = useState(3);
  const [desc, setDesc] = useState('');
  const [checkIns, setCheckIns] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);
  // Simplified streak system state - only increases
  const [streak, setStreak] = useState(0);

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (transcript) {
      setDesc(transcript);
    }
  }, [transcript]);

  const startListening = () => {
    resetTranscript();
    SpeechRecognition.startListening({ continuous: true, language: 'en-IN' });
  };

  const stopListening = () => {
    SpeechRecognition.stopListening();
  };

  const fetchCheckIns = useCallback(async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, "checkins"),
        where("userId", "==", user.uid)
      );
      const querySnapshot = await getDocs(q);
      const entries = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          formattedTime: format(new Date(data.timestamp), 'MMM d, HH:mm'),
          dateOnly: format(new Date(data.timestamp), 'yyyy-MM-dd')
        };
      });
      const sortedEntries = entries.sort((a, b) => a.timestamp - b.timestamp);
      setCheckIns(sortedEntries);

      // Calculate streak based on actual unique days
      let currentStreak = 0;
      if (sortedEntries.length > 0) {
        const uniqueDates = Array.from(new Set(sortedEntries.map(e => e.dateOnly)));
        currentStreak = uniqueDates.length;
      }
      setStreak(currentStreak);
    } catch (error) {
      console.error("Error fetching check-ins:", error);
    }
  }, [user]);

  const submitCheckIn = async () => {
    if (!user) {
      alert("Please login to save check-ins");
      navigate('/login');
      return;
    }

    setIsSubmitting(true);
    const todayKey = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const newData = {
      userId: user.uid,
      userEmail: user.email,
      moodRating,
      moodDescription: MOOD_LABELS[moodRating],
      description: desc,
      timestamp: Date.now(),
      dateOnly: todayKey
    };

    try {
      // Add check-in data to Firestore with user ID
      await addDoc(collection(db, "checkins"), newData);

      // Award 50 points for each unique day check-in
      // Check if this is the first check-in for today
      const checkinCol = collection(db, "checkins");
      const qToday = query(checkinCol, where("userId", "==", user.uid), where("dateOnly", "==", todayKey));
      const todaySnapshot = await getDocs(qToday);
      if (todaySnapshot.size === 1) { // First check-in for today
        // Award 50 points
        const { doc, getDoc, setDoc } = await import('firebase/firestore');
        const userDocRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userDocRef);
        let newScore = 50;
        if (userSnap.exists()) {
          const userData = userSnap.data();
          newScore = (userData.wellnessScore || 0) + 50;
        }
        await setDoc(userDocRef, { wellnessScore: newScore }, { merge: true });
      }

      // Store latest mood data for recommendations
      const moodData = {
        mood: `${MOOD_LABELS[moodRating]} - ${desc}`.trim(),
        moodDescription: MOOD_LABELS[moodRating],
        description: desc,
        moodRating: moodRating,
        timestamp: Date.now(),
        userId: user.uid
      };
      localStorage.setItem('latestMoodData', JSON.stringify(moodData));
      setDesc('');
      fetchCheckIns();
      setShowSuggestion(true); // Show smart popup after check-in
    } catch (error) {
      alert("❌ Failed to submit.");
      console.error(error);
    }
    setIsSubmitting(false);
  };

  const formattedHistory = useMemo(() => {
    return checkIns.map(record => ({
      ...record,
      date: record.formattedTime
    }));
  }, [checkIns]);

  // Submit on Enter (without Shift) in description textarea
  const handleDescKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSubmitting) submitCheckIn();
    }
  };

  useEffect(() => {
    if (user) {
      fetchCheckIns();
    }
  }, [user, fetchCheckIns]);

  // Page-specific background class
  useEffect(() => {
    const rootEl = document.getElementById('root');
    if (rootEl) rootEl.classList.add('checkin-bg');
    return () => { if (rootEl) rootEl.classList.remove('checkin-bg'); };
  }, []);

  // Show loading while checking authentication
  if (loading) return <div className="loading-container">Loading...</div>;

  // Don't render if user is not authenticated
  if (!user) return null;

  // Smart suggestion logic
  const getSuggestions = () => {
    if (moodRating <= 2) {
      return [
        { label: 'Try Meditation', link: '/Game' },
        { label: 'Chat with AI Assistant', link: '/assistant' },
        { label: 'Book a Therapist', link: '/Book' }
      ];
    } else if (moodRating === 3) {
      return [
        { label: 'Try Meditation', link: '/Game' },
        { label: 'Chat with AI Assistant', link: '/assistant' }
      ];
    } else {
      // Good (4) or Very Good (5) mood
      return [
        { label: 'Do Daily Task', link: '/task' },
        { label: 'Explore Mindfulness Library', link: '/library' },
        { label: 'Chat with Community', link: '/community' }
      ];
    }
  };

  return (
    <div className="checkin-container">
      <div className="streak-badge" style={{ position: 'absolute', top: 20, right: 20, zIndex: 10 }}>
        <span style={{ background: '#ffe066', borderRadius: '20px', padding: '8px 16px', fontWeight: 'bold', boxShadow: '0 2px 8px #eee', display: 'flex', alignItems: 'center' }}>
          🔥 Streak: {streak}
        </span>
      </div>
      <h2>Your Mood Check‑In</h2>
      <p className="welcome-message">Hi {user.displayName || user.email}, lets start the day with a checkin.</p>

      <label className="mood-level-label">
        <span className="mood-level-text">Mood Level:</span> <span className="mood-current">{MOOD_LABELS[moodRating]}</span> {MOOD_ICONS[moodRating]}
      </label>

      <input
        type="range"
        min="1"
        max="5"
        value={moodRating}
        onChange={(e) => setMoodRating(Number(e.target.value))}
      />

      <div className="mood-labels">
        <span>{MOOD_ICONS[1]}</span>
        <span>{MOOD_ICONS[2]}</span>
        <span>{MOOD_ICONS[3]}</span>
        <span>{MOOD_ICONS[4]}</span>
        <span>{MOOD_ICONS[5]}</span>
      </div>

      <label>Description:</label>
      <div className="textarea-row">
        <textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onKeyDown={handleDescKeyDown}
          placeholder="Type or speak about your mood..."
        />
        <button
          onClick={listening ? stopListening : startListening}
          className={`mic-btn ${listening ? 'active' : ''}`}
          title={listening ? 'Stop Listening' : 'Start Listening'}
        >
          {listening ? <MicOff size={20} /> : <Mic size={20} />}
        </button>
      </div>

      {!browserSupportsSpeechRecognition && (
        <p>Your browser does not support speech recognition.</p>
      )}

      <button className="save-checkin-btn" onClick={submitCheckIn} disabled={isSubmitting}>
        {isSubmitting ? 'Saving...' : 'Save Check-In'}
      </button>

      {formattedHistory.length > 0 && (
        <>
          <hr style={{ margin: '60px 0 50px 0', border: 'none', borderTop: '5px solid #d0d0d0' }} />
          <div className="history-block">
            <h3>Your Mood History</h3>
          <div className="history-chart-box">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={formattedHistory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" fontSize={12} />
                <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} />
                <Tooltip
                  formatter={(value, name, props) =>
                    [`${MOOD_LABELS[value]} ${props.payload.description ? ` - ${props.payload.description}` : ''}`, 'Mood']}
                />
                <Line
                  type="monotone"
                  dataKey="moodRating"
                  stroke="#8884d8"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        </>
      )}

      {/* Smart Suggestion Popup Modal */}
      {showSuggestion && (
        <div className="smart-popup-overlay">
          <div className="smart-popup-modal">
            <span
              className="smart-popup-close"
              onClick={() => setShowSuggestion(false)}
              aria-label="Close"
              style={{ position: 'absolute', top: 1, right: 4, fontSize: 32, color: '#e74c3c', cursor: 'pointer', fontWeight: 'bold', background: 'none', border: 'none' }}
            >x</span>
            <h3>What would you like to do next?</h3>

            <div className="smart-popup-actions">
              {getSuggestions().map((s, idx) => (
                <button
                  key={s.label}
                  className="smart-popup-btn"
                  onClick={() => {
                    setShowSuggestion(false);
                    navigate(s.link);
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CheckInPage;